import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Warehouse } from "lucide-react";
import { useViewAsClient } from "@/lib/viewAsClient";
import { decomposeInSiteTz } from "@/lib/datetime";
import BookingForm from "@/components/BookingForm";
import StatusLegend from "./dock-manager/StatusLegend";
import DockToolbar from "./dock-manager/DockToolbar";
import DockGrid from "./dock-manager/DockGrid";
import { useDockData } from "./dock-manager/useDockData";
import { useGridModel, snapToHour } from "./dock-manager/gridModel";
import { setDisplayTz } from "./dock-manager/formatters";

// Scaled so the whole window fits ~1920px without horizontal scrolling.
const PX_PER_HOUR_BY_HORIZON = { 24: 80, 48: 48, 72: 32 };
const PX_PER_HOUR_DEFAULT = 80;
const NOW_LEFT_OFFSET_PX = 240; // how far from the lane's left edge to park "now"

const initialAnchor = (searchParams) => {
  const d = new Date(searchParams.get("anchor") || "");
  return snapToHour(isNaN(d.getTime()) ? new Date() : d);
};

// Site + anchor live in the URL so back/refresh keeps your place.
function useGridLocation() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [siteId, setSiteId] = useState(() => searchParams.get("site") || "");
  const [anchor, setAnchor] = useState(() => initialAnchor(searchParams));
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (siteId) next.set("site", siteId); else next.delete("site");
    next.set("anchor", anchor.toISOString());
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- write-only sync; adding searchParams would loop
  }, [siteId, anchor]);
  return { siteId, setSiteId, anchor, setAnchor };
}

function useScrollToNow(scrollRef, windowStart, windowEnd, pxPerHour) {
  useEffect(() => {
    if (!scrollRef.current) return;
    const now = new Date();
    if (now < windowStart || now > windowEnd) return;
    const px = ((now - windowStart) / 3600 / 1000) * pxPerHour - NOW_LEFT_OFFSET_PX;
    scrollRef.current.scrollLeft = Math.max(0, px);
  }, [scrollRef, windowStart, windowEnd, pxPerHour]);
}

// New-booking prefill for a click on an empty hour slot.
function slotPrefill({ bays, site, siteId, siteTz, windowStart }, bayId, hourOffset) {
  const slotStart = new Date(windowStart.getTime() + hourOffset * 3600 * 1000);
  const bay = bays.find((b) => b.id === bayId);
  const slotMin = bay?.slot_duration_minutes || site?.slot_duration_minutes || 60;
  const s = decomposeInSiteTz(slotStart.toISOString(), siteTz);
  const e = decomposeInSiteTz(new Date(slotStart.getTime() + slotMin * 60000).toISOString(), siteTz);
  return { site_id: siteId, bay_id: bayId, booking_date: s.dateStr, start_time: s.timeStr, end_time: e.timeStr, timezone: siteTz };
}

export default function DockManager() {
  const { viewAsClientId } = useViewAsClient();
  const { siteId, setSiteId, anchor, setAnchor } = useGridLocation();
  const [horizon, setHorizon] = useState(48);
  const [editing, setEditing] = useState(null); // booking row OR { __new: true, __prefill }
  const scrollRef = useRef(null);

  const windowStart = anchor;
  const windowEnd = useMemo(() => new Date(anchor.getTime() + horizon * 3600 * 1000), [anchor, horizon]);
  const data = useDockData({ viewAsClientId, siteId, setSiteId, windowStart, windowEnd });
  const model = useGridModel({ data, siteId, windowStart, windowEnd, horizonHours: horizon });

  const siteTz = data.activeSite?.timezone || "Australia/Melbourne";
  setDisplayTz(siteTz);
  // Initial anchor was snapped in browser time; re-snap once the site tz is known (e.g. Adelaide +:30).
  useEffect(() => {
    if (!data.activeSite?.timezone) return;
    setAnchor((a) => {
      const snapped = snapToHour(a, siteTz);
      return snapped.getTime() === a.getTime() ? a : snapped;
    });
  }, [data.activeSite?.timezone, siteTz, setAnchor]);
  const pxPerHour = PX_PER_HOUR_BY_HORIZON[horizon] ?? PX_PER_HOUR_DEFAULT;
  useScrollToNow(scrollRef, windowStart, windowEnd, pxPerHour);

  const jumpBy = (hours) => setAnchor((a) => snapToHour(new Date(a.getTime() + hours * 3600 * 1000), siteTz));
  const nav = {
    onPrev: () => jumpBy(-horizon),
    onNow: () => setAnchor(snapToHour(new Date(), siteTz)),
    onNext: () => jumpBy(horizon),
    onRefresh: data.refresh,
  };
  const handleSlotClick = (bayId, hourOffset) => setEditing({
    __new: true,
    __prefill: slotPrefill({ bays: data.bays, site: data.activeSite, siteId, siteTz, windowStart }, bayId, hourOffset),
  });
  const handleSaved = (saved) => {
    // Jump to the saved booking's site so the new card doesn't appear to vanish.
    const savedSiteId = saved?.site?.id || saved?.site_id || null;
    if (savedSiteId && savedSiteId !== siteId) setSiteId(savedSiteId);
    data.refresh();
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div data-testid="dock-manager-page" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Warehouse className="h-6 w-6 text-slate-700" /> Dock manager
          </h1>
          <p className="text-sm text-slate-500">
            Rolling {horizon}h grid of bay activity. Click a card to edit; click an empty slot to add.
          </p>
        </div>
      </div>
      <DockToolbar site={data.activeSite} sites={data.sites} siteId={siteId} setSiteId={setSiteId} horizon={horizon}
                   setHorizon={setHorizon} nav={nav} windowStart={windowStart} windowEnd={windowEnd}
                   siteTz={siteTz} loading={data.loading} />
      <DockGrid ref={scrollRef} data={data} model={model} windowStart={windowStart} windowEnd={windowEnd}
                pxPerHour={pxPerHour} horizonHours={horizon}
                onSlotClick={handleSlotClick} onBookingClick={setEditing} />
      <StatusLegend />
      <BookingForm
        open={!!editing}
        onOpenChange={(open) => !open && setEditing(null)}
        booking={editing?.__new ? null : editing}
        defaultSiteId={editing?.__prefill?.site_id}
        defaultBayId={editing?.__prefill?.bay_id}
        prefill={editing?.__prefill}
        onSaved={handleSaved}
      />
    </div>
    </TooltipProvider>
  );
}
